import React, { useCallback, useEffect, useRef, useState } from 'react';

const SLIDER_SCRUB_THRESHOLD = 5;
const SLIDER_SCRUB_TOUCH_THRESHOLD = 8;

function isSliderScrubBlockedTarget(target: HTMLElement): boolean {
  if (target.closest('.ant-slider-mark-text, .mark, .mark_tooltip_side')) {
    return true;
  }

  const markTimeline = target.closest('.mark_timeline');
  if (!markTimeline) return false;

  return (
    markTimeline.classList.contains('mark_timeline_edit_mode') ||
    markTimeline.classList.contains('mark_timeline_borders')
  );
}

type UseSliderScrubProps = {
  trackRef: React.MutableRefObject<HTMLDivElement | null>;
  onScrub: (clientX: number) => void;
};

export default function useSliderScrub({
  trackRef,
  onScrub
}: UseSliderScrubProps) {
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubPercent, setScrubPercent] = useState<number | null>(null);
  const isScrubbingRef = useRef(false);
  const scrubStartRef = useRef<{
    mouseX: number;
    mouseY: number;
    pointerId: number;
    pointerType: string;
  } | null>(null);

  const getScrubPercent = useCallback(
    (clientX: number) => {
      const lineEl = trackRef.current;
      if (!lineEl) return null;

      const rect = lineEl.getBoundingClientRect();
      return Math.min(
        100,
        Math.max(0, ((clientX - rect.left) / rect.width) * 100)
      );
    },
    [trackRef]
  );

  const endScrub = useCallback(() => {
    const scrubStart = scrubStartRef.current;
    if (trackRef.current && scrubStart) {
      trackRef.current.releasePointerCapture?.(scrubStart.pointerId);
    }

    scrubStartRef.current = null;
    isScrubbingRef.current = false;
    setIsScrubbing(false);
    setScrubPercent(null);
  }, [trackRef]);

  const onTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      if (isSliderScrubBlockedTarget(e.target as HTMLElement)) return;

      scrubStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        pointerId: e.pointerId,
        pointerType: e.pointerType
      };
    },
    []
  );

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!scrubStartRef.current) return;
      if (scrubStartRef.current.pointerId !== e.pointerId) return;

      if (!isScrubbingRef.current) {
        const deltaX = e.clientX - scrubStartRef.current.mouseX;
        const deltaY = e.clientY - scrubStartRef.current.mouseY;
        const threshold =
          scrubStartRef.current.pointerType === 'touch'
            ? SLIDER_SCRUB_TOUCH_THRESHOLD
            : SLIDER_SCRUB_THRESHOLD;

        if (Math.hypot(deltaX, deltaY) < threshold) return;

        isScrubbingRef.current = true;
        setIsScrubbing(true);
        trackRef.current?.setPointerCapture?.(e.pointerId);
      }

      const percent = getScrubPercent(e.clientX);
      if (percent === null) return;

      e.preventDefault();
      setScrubPercent(percent);
      onScrub(e.clientX);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!scrubStartRef.current) return;
      if (scrubStartRef.current.pointerId !== e.pointerId) return;

      if (isScrubbingRef.current) {
        endScrub();
        return;
      }

      scrubStartRef.current = null;
    };

    document.addEventListener('pointermove', onPointerMove, { passive: false });
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);

    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };
  }, [endScrub, getScrubPercent, onScrub, trackRef]);

  return {
    isScrubbing,
    scrubPercent,
    isScrubbingRef,
    onTrackPointerDown
  };
}
